/** FuryBot — Free Text Intent Router (NLU-lite) */
import { formatConsistency } from "@/utils/firmRules";
import { getSizeNumeric, findByNumericSize, numToSizeLabel } from "@/utils/sizeUtils";
import { parsePrice } from "@/utils/priceUtils";
import { formatFirmDisplay } from "@/utils/formatFirmDisplay";
import { computeTrueCost } from "@/utils/trueCostUtils";
import { estimatePathDays } from "@/utils/pathEstimator";
import { computeFFScore, getBenchmarkTier } from "@/utils/furyScore";
import { getWhyFirmFits } from "@/utils/getWhyFirmFits";
import { logQuery } from "../utils/analytics";
import { isProfane } from "../utils/filters";

// ═══ CENTRALIZED INTENT PATTERNS ═══
const SIZE_RE = /\b(25|50|100|150|200|250|300)[,.]?0{0,3}\s*k\b/i;
const DEAL_INTENT = /deal|discount|code|promo|coupon|save|got a code/;
const COMPARE_INTENT = /compare|vs\b|versus/;
const TRUE_COST_INTENT = /true.?cost|real.?cost|total.?cost|actual.?cost|how much.*really|full.?cost|cost.*funded|cost.*get.*funded|reset.?fee|activation.?fee|hidden.?fee|hidden.?cost/;
const INSTANT_INTENT = /instant.?fund|skip.*eval|no.*eval|without.*eval|direct.*fund|funded.*instant|funded.*immediate|instant.*account/;
const SENTIMENT_INTENT = /legit|scam|trust(worthy)?|review|reputation|what.*people.*say|sentiment|community.*say|reliable|safe to|worth it|good firm|bad firm|can i trust/;
const PRICE_INTENT = /cheap|lowest.?cost|afford|eval.*price|firm.*cost|budget.*eval|evaluation.*cost|cheapest.*eval|low.?cost|lowest.?price|best.?price|best.?deal.*eval|budget.?prop/;
const PAYOUT_INTENT = /payout|pay.*fast|fast.*pay|quick.*pay|quickest.*pay|fastest.*pay|who pays|how fast.*pay|how quick.*pay|payout.*time|withdrawal.*speed|how long.*paid|get paid/;
const SPLIT_INTENT = /profit.*split|best.*split|highest.*split|keep.*most.*profit|payout.*percent|90%.*split|95%.*split|100%.*split|who gives.*most/;
const CONSISTENCY_INTENT = /consistency|no cons|cons rule|best day.*rule|without consistency|consistency.*percent|consistency.*require/;
const DRAWDOWN_INTENT = /static.?dd|static.*drawdown|trailing.?dd|trailing.*draw|eod.*draw|end.of.day.*draw|dd.?type|drawdown.*type|which.*drawdown|what.*drawdown|drawdown.*rule|intraday.*draw|real.?time.*draw/;
const NEWS_INTENT = /news.?trad|trade.?news|cpi|fomc|nfp|economic.*calendar|news.*restrict|news.*event|news.*allow/;
const SCALP_INTENT = /scalp|hold.?time|min.*hold|minimum.*hold|quick.?trade|fast.?trade/;
const SCALE_INTENT = /scal(e|ing).*plan|best.*scal(e|ing)|grow.*account|scale.*up|biggest.*account.*possible|increase.*account/;
const SIZE_INTENT = /account.*size|what.*size|size.*available|account.*option|\b(25|50|100|150|200|250)k\s*account/i;
const EA_INTENT = /\bea\b|expert.?advisor|bot.*allow|algo.*trad|algorithmic|automat.*trad|can.*use.*bot/;
const COPY_INTENT = /copy.?trad|trade.?copier|signal.*copy|mirror.*trad/;
const GOTCHA_INTENT = /gotcha|catch(es)?|watch.?out|pitfall|downside|\bcons\b|red.?flag|beware|careful|trick|trap|warning|fine.?print|what.*wrong|drawback|negative/;
const PAYOUT_PATH_INTENT = /path.*payout|first.*payout|when.*first.*pa|timeline.*payout|how long.*funded|days.*payout|days to.*pa|time to.*first.*pa|payout.*timeline|when.*get.*paid.*first|how.*quickly.*paid/;
const TIME_LIMIT_INTENT = /time.?limit|deadline.*eval|evaluation.*deadline|unlimited.*time|no.*time.*limit|time.*complete.*eval|how long.*complete|days.*finish.*eval/;
const MARKET_PULSE_INTENT = /market.?pulse|market.*today|vix|volatility|should.*trade|market.*mood|market.*condition|how.*market/;
const BEGINNER_INTENT = /new to|beginner|start.*trad|first.*time|learn|getting.*start/;
const RULE_INTENT = /\brule\b|explain/;
const TOOL_INTENT = /tool|calculator|visualiz/;
const OFF_TOPIC = /bitcoin|btc|crypto|forex|stock|casino|gambling|weather|sport/;

// Helper: extract account size from text
const extractSize = (txt) => { const m = txt.match(SIZE_RE); return m ? parseInt(m[1]) * 1000 : null; };

// Helper: build structured trust-format answer for firm + rule questions
const buildFirmRuleAnswer = (firm, ruleName, ruleValue, ruleIcon, highlights) => {
  const lines = [`**${firm.name}**\n`];
  lines.push(`${ruleIcon} ${ruleName}: ${ruleValue}\n`);
  if (highlights.length > 0) {
    lines.push(`Why traders choose ${firm.name.split(" ")[0]}:`);
    highlights.forEach(h => lines.push(`\u2022 ${h}`));
  }
  return lines.join("\n");
};

// Helper: get firm highlights from live data
/** Context-aware firm highlights using SSOT getWhyFirmFits */
const getFirmHighlights = (firm, intent = null) => {
  if (intent) {
    const bullets = getWhyFirmFits(intent, firm);
    if (bullets.length >= 2) return bullets;
  }
  // Fallback: pick intent based on firm's strongest trait
  const fd = formatFirmDisplay(firm);
  const act = firm.activation_fee;
  if (act && (act === "$0" || act.toLowerCase() === "none")) return getWhyFirmFits("no-activation", firm);
  if (fd.profitSplit?.includes("100")) return getWhyFirmFits("high-profit-split", firm);
  return getWhyFirmFits("fast-payouts", firm);
};

/**
 * Process free-text input and route to the appropriate handler.
 * Returns true if handled, false if fell through to unknown.
 */
export function processFreeText(text, ctx) {
  const { firms: f, addBot, detectFirm, startMatchFlow, startDealsFlow, startCompareFlow, startRulesFlow, startBeginnerFlow, startToolsFlow, startMarketPulse, showFirmDeal } = ctx;
  const lower = text.toLowerCase();

  // Profanity filter
  if (isProfane(lower)) {
    addBot("I'm here to help you find the best futures prop firm. Let's keep it productive \u2014 what can I help you with?", [
      { label: "Help me pick a firm", action: "flow_match" },
      { label: "Live deals", action: "flow_deals" },
      { label: "Compare firms", action: "flow_compare" },
      { label: "Main menu", action: "home" },
    ]);
    return;
  }

  const firmSlug = detectFirm(lower);
  const firmObj = firmSlug ? f.find(ff => ff.slug === firmSlug) : null;

  // ═══ OFF-TOPIC REDIRECT ═══
  if (OFF_TOPIC.test(lower) && !DEAL_INTENT.test(lower) && !firmSlug) {
    logQuery(text, "off_topic", null);
    return addBot("I'm not sure I understand that. I can help you with futures prop firm questions \u2014 here's what I can do:", [
      { label: "Help me pick a firm", action: "flow_match" },
      { label: "Live deals", action: "flow_deals" },
      { label: "Compare firms", action: "flow_compare" },
      { label: "Explain rules", action: "flow_rules" },
    ]);
  }

  // ═══ DEAL / COUPON / PROMO ═══
  if (DEAL_INTENT.test(lower)) {
    logQuery(text, "deal", firmSlug);
    if (firmSlug) return showFirmDeal(firmSlug);
    return startDealsFlow();
  }

  // ═══ COMPARE ═══
  if (COMPARE_INTENT.test(lower)) { logQuery(text, "compare", firmSlug); return startCompareFlow(); }

  // ═══ TRUE COST TO FUNDED ═══
  if (TRUE_COST_INTENT.test(lower)) {
    logQuery(text, "true_cost", firmSlug);
    const size = extractSize(lower) || 50000;
    const sizeLabel = numToSizeLabel(size);
    if (firmObj) {
      const p = findByNumericSize(firmObj.pricing, size);
      const tc = p ? computeTrueCost(firmObj, p) : null;
      if (tc) {
        const lines = [`**${firmObj.name} \u2014 True Cost to Funded (${sizeLabel}):**\n`];
        lines.push(`\u2022 Eval fee: ${tc.components.eval.display}${tc.components.eval.label !== "Eval fee" ? ` (${tc.components.eval.label})` : ""}`);
        lines.push(`\u2022 Activation: ${tc.components.activation.display}${tc.components.activation.note ? ` \u2014 ${tc.components.activation.note}` : ""}`);
        lines.push(`\u2022 1 Reset: ${tc.components.reset.display}${tc.components.reset.note ? ` \u2014 ${tc.components.reset.note}` : ""}`);
        lines.push(`\n**Total: ${tc.display}** (${tc.confidence})`);
        const fd = formatFirmDisplay(firmObj);
        if (fd.discountValid) lines.push(`\n_Active ${fd.discountPercent}% discount available \u2014 lowers your eval cost further._`);
        return addBot(lines.join("\n"), [
          { label: `Get ${firmObj.name} deal`, action: "link", data: `/${firmObj.slug}-discount` },
          { label: "True Cost comparison", action: "link", data: "/true-cost-prop-firm-comparison" },
          { label: "Compare side by side", action: "flow_compare" },
          { label: "Main menu", action: "home" },
        ]);
      }
    }
    // All firms ranked by true cost
    const ranked = [...f].filter(ff => ff.pricing?.length).map(ff => {
      const p = findByNumericSize(ff.pricing, size);
      const tc = p ? computeTrueCost(ff, p) : null;
      return { firm: ff, tc };
    }).filter(r => r.tc).sort((a, b) => a.tc.totalCost - b.tc.totalCost);
    const cheapestCost = ranked[0]?.tc?.totalCost || Infinity;
    let answer = `**True Cost to Funded \u2014 ${sizeLabel} (cheapest first):**\n_Best if you want to minimize your upfront cost and risk._\n`;
    ranked.slice(0, 6).forEach(({ firm: ff, tc }, i) => {
      let why = "";
      if (i === 0) why = " \u2190 lowest total cost";
      else if (tc.confidence === "exact") why = " \u2190 exact breakdown";
      else {
        const fd = formatFirmDisplay(ff);
        if (fd.discountValid) why = " \u2190 active discount";
      }
      answer += `\u2022 ${ff.name} \u2014 ${tc.display} (${tc.confidence})${why}\n`;
    });
    answer += `\n_See all current deals before deciding \u2014 some firms have limited-time discounts._`;
    const top2Btns = ranked.length >= 2 ? [{ label: `Compare ${ranked[0].firm.name.split(" ")[0]} vs ${ranked[1].firm.name.split(" ")[0]}`, action: "compare_size", data: JSON.stringify({ s1: ranked[0].firm.slug, s2: ranked[1].firm.slug, size }) }] : [];
    return addBot(answer, [
      ...top2Btns,
      { label: "See all current deals", action: "flow_deals" },
      { label: "Full True Cost table", action: "link", data: "/true-cost-prop-firm-comparison" },
      { label: "Main menu", action: "home" },
    ]);
  }

  // ═══ INSTANT FUNDING ═══
  if (INSTANT_INTENT.test(lower)) {
    logQuery(text, "instant_funding", firmSlug);
    const hasInstant = ff => ff.supported_eval_types?.some(t => t.toLowerCase().includes("instant"));
    if (firmObj) {
      const instant = hasInstant(firmObj);
      const fd = formatFirmDisplay(firmObj);
      const icon = instant ? "\u2705 Yes \u2014 instant funding available" : "\u274c No instant funding option";
      const lines = [`**${firmObj.name}**\n`, `${icon}\n`];
      if (instant) {
        const types = (firmObj.supported_eval_types || []).filter(t => t.toLowerCase().includes("instant"));
        lines.push(`Instant plan: ${types.join(", ")}`);
        lines.push(`\n_Skip the evaluation and start trading funded right away._`);
      }
      lines.push(`\nEval types: ${(firmObj.supported_eval_types || []).join(", ")}`);
      const highlights = getFirmHighlights(firmObj);
      if (highlights.length) { lines.push(`\nWhy traders choose ${firmObj.name.split(" ")[0]}:`); highlights.forEach(h => lines.push(`\u2022 ${h}`)); }
      return addBot(lines.join("\n"), [
        instant ? { label: `Get ${firmObj.name} deal`, action: "link", data: `/${firmObj.slug}-discount` } : { label: `${firmObj.name} profile`, action: "link", data: `/firms/${firmObj.slug}` },
        { label: "All instant funding firms", action: "link", data: "/prop-firms-with-instant-funding" },
        { label: "Compare side by side", action: "flow_compare" },
        { label: "Main menu", action: "home" },
      ]);
    }
    const instantFirms = f.filter(hasInstant);
    const noInstant = f.filter(ff => !hasInstant(ff));
    let answer = `**Firms with instant funding options:**\n_Best if you want to skip evaluations and get funded faster._\n`;
    if (instantFirms.length) {
      instantFirms.forEach(ff => {
        const types = (ff.supported_eval_types || []).filter(t => t.toLowerCase().includes("instant"));
        const fd = formatFirmDisplay(ff);
        const why = ff.payout_frequency?.toLowerCase().includes("daily") ? " \u2190 daily payouts" : fd.discountValid ? " \u2190 active discount" : "";
        answer += `\u2022 ${ff.name} \u2014 ${types.join(", ")} | ${fd.drawdownShort} | ${fd.profitSplit}${why}\n`;
      });
    } else {
      answer += "No firms currently offer instant funding in our data.\n";
    }
    if (noInstant.length) {
      answer += `\n**Evaluation-only (no instant funding):**\n`;
      noInstant.forEach(ff => { answer += `\u2022 ${ff.name}\n`; });
    }
    answer += `\n_Check current deals \u2014 some instant funding plans have limited-time discounts._`;
    const top2Instant = instantFirms.length >= 2 ? [{ label: `Compare ${instantFirms[0].name.split(" ")[0]} vs ${instantFirms[1].name.split(" ")[0]}`, action: "compare_size", data: JSON.stringify({ s1: instantFirms[0].slug, s2: instantFirms[1].slug, size: 50000 }) }] : [];
    return addBot(answer, [
      ...top2Instant,
      { label: "See all current deals", action: "flow_deals" },
      { label: "Instant funding firms", action: "link", data: "/prop-firms-with-instant-funding" },
      { label: "Main menu", action: "home" },
    ]);
  }

  // ═══ CHEAPEST / PRICE ═══
  if (PRICE_INTENT.test(lower)) {
    logQuery(text, "price", firmSlug);
    const size = extractSize(lower) || 50000;
    const sizeLabel = numToSizeLabel(size);
    const sorted = [...f].filter(ff => ff.pricing?.length).sort((a, b) => {
      const pa = parsePrice(findByNumericSize(a.pricing, size)?.price);
      const pb = parsePrice(findByNumericSize(b.pricing, size)?.price);
      return pa - pb;
    });
    if (firmObj) {
      const p = findByNumericSize(firmObj.pricing, size);
      const priceDisplay = p ? formatFirmDisplay(firmObj, p).priceFormatted : "Check site";
      const fd = formatFirmDisplay(firmObj, p);
      const lines = [`**${firmObj.name}** ${sizeLabel} evaluation: ${priceDisplay}`];
      if (fd.discountValid) lines.push(`\n_Active ${fd.discountPercent}% discount \u2014 price shown includes discount._`);
      return addBot(lines.join("\n"), [
        { label: `Get ${firmObj.name} deal`, action: "link", data: `/${firmObj.slug}-discount` },
        { label: "All cheapest firms", action: "link", data: "/cheapest-futures-prop-firms" },
        { label: "Main menu", action: "home" },
      ]);
    }
    let answer = `**Cheapest ${sizeLabel} evaluations:**\n_Best options if you want to keep upfront costs low._\n`;
    sorted.slice(0, 6).forEach((ff, i) => {
      const p = findByNumericSize(ff.pricing, size);
      const fd = formatFirmDisplay(ff, p);
      const why = i === 0 ? " \u2190 cheapest" : fd.discountValid ? " \u2190 active discount" : "";
      answer += `\u2022 ${ff.name} \u2014 ${p ? fd.priceFormatted : "N/A"}${why}\n`;
    });
    answer += `\n_Prices may be lower with active discounts. Check deals for latest pricing._`;
    const top2Price = sorted.length >= 2 ? [{ label: `Compare ${sorted[0].name.split(" ")[0]} vs ${sorted[1].name.split(" ")[0]}`, action: "compare_size", data: JSON.stringify({ s1: sorted[0].slug, s2: sorted[1].slug, size }) }] : [];
    return addBot(answer, [
      ...top2Price,
      { label: "See all current deals", action: "flow_deals" },
      { label: "Cheapest firms", action: "link", data: "/cheapest-futures-prop-firms" },
      { label: "Main menu", action: "home" },
    ]);
  }

  // ═══ PATH TO FIRST PAYOUT ═══
  if (PAYOUT_PATH_INTENT.test(lower)) {
    logQuery(text, "payout_path", firmSlug);
    if (firmObj) {
      const lines = [`**${firmObj.name} \u2014 Path to First Payout:**\n`];
      if (firmObj.first_payout_days) lines.push(`\ud83d\udcc5 First payout: ${firmObj.first_payout_days}`);
      if (firmObj.funded_min_days) lines.push(`\ud83d\udcc8 Min funded trading days: ${firmObj.funded_min_days}`);
      if (firmObj.activation_fee) lines.push(`\u2705 Activation: ${firmObj.activation_fee}`);
      if (firmObj.payout_frequency) lines.push(`\ud83d\udcb0 Payout cycle: ${firmObj.payout_frequency}`);
      if (firmObj.consistency_rule && !firmObj.consistency_rule.toLowerCase().includes("none")) {
        lines.push(`\u26a0\ufe0f Consistency: ${firmObj.consistency_rule}`);
      }
      if (firmObj.payout_methods?.length) lines.push(`\ud83d\udcb3 Methods: ${firmObj.payout_methods.join(", ")}`);
      return addBot(lines.join("\n"), [
        { label: `${firmObj.name} profile`, action: "link", data: `/firms/${firmObj.slug}` },
        { label: `${firmObj.name} discount`, action: "link", data: `/${firmObj.slug}-discount` },
        { label: "Compare all payouts", action: "link", data: "/prop-firms-fast-payouts" },
        { label: "Main menu", action: "home" },
      ]);
    }
    // Rank all firms by fastest path to payout (SSOT: estimatePathDays)
    const sorted = [...f].map(ff => ({ firm: ff, days: estimatePathDays(ff)?.total ?? 999 })).sort((a, b) => a.days - b.days);
    let answer = `**Path to First Payout (fastest first):**\n_Best if you want to start earning as quickly as possible._\n`;
    sorted.forEach(({ firm: ff, days }, i) => {
      const tag = i === 0 ? " \u2190 fastest" : days <= 7 ? " \u2190 quick" : "";
      answer += `\u2022 ${ff.name} \u2014 ~${days} days | ${ff.payout_frequency || "See site"}${tag}\n`;
    });
    answer += `\n_Check active deals \u2014 some firms offer discounted evaluations right now._`;
    const top2Path = sorted.length >= 2 ? [{ label: `Compare ${sorted[0].firm.name.split(" ")[0]} vs ${sorted[1].firm.name.split(" ")[0]}`, action: "compare_size", data: JSON.stringify({ s1: sorted[0].firm.slug, s2: sorted[1].firm.slug, size: 50000 }) }] : [];
    return addBot(answer, [
      ...top2Path,
      { label: "See all current deals", action: "flow_deals" },
      { label: "Fastest payouts", action: "link", data: "/prop-firms-fast-payouts" },
      { label: "Main menu", action: "home" },
    ]);
  }

  // ═══ PAYOUT SPEED ═══
  if (PAYOUT_INTENT.test(lower) && !SPLIT_INTENT.test(lower)) {
    logQuery(text, "payout", firmSlug);
    if (firmObj) {
      const fd = formatFirmDisplay(firmObj);
      const answer = buildFirmRuleAnswer(firmObj, "Payout", `${fd.payoutSchedule} | Profit Split: ${fd.profitSplit}`, "\ud83d\udcb0", getFirmHighlights(firmObj, "fast-payouts"));
      return addBot(answer, [
        { label: `${firmObj.name} profile`, action: "link", data: `/firms/${firmObj.slug}` },
        { label: "All firms' payouts", action: "link", data: "/prop-firms-fast-payouts" },
        { label: "Main menu", action: "home" },
      ]);
    }
    const sorted = [...f].map(ff => ({ firm: ff, days: estimatePathDays(ff)?.total ?? 999 })).sort((a, b) => a.days - b.days);
    let answer = `**Firms by payout speed:**\n_Faster payouts mean you access your profits sooner._\n`;
    sorted.forEach(({ firm: ff, days }) => {
      const tag = days <= 7 ? " \u2190 fast" : "";
      answer += `\u2022 ${ff.name} \u2014 ~${days} days | ${ff.payout_frequency || "See site"} | ${ff.profit_split || "\u2014"}${tag}\n`;
    });
    const top2Payout = sorted.length >= 2 ? [{ label: `Compare ${sorted[0].firm.name.split(" ")[0]} vs ${sorted[1].firm.name.split(" ")[0]}`, action: "compare_size", data: JSON.stringify({ s1: sorted[0].firm.slug, s2: sorted[1].firm.slug, size: 50000 }) }] : [];
    return addBot(answer, [
      ...top2Payout,
      { label: "See all current deals", action: "flow_deals" },
      { label: "Fastest payouts", action: "link", data: "/prop-firms-fast-payouts" },
      { label: "Main menu", action: "home" },
    ]);
  }

  // ═══ CONSISTENCY RULE ═══
  if (CONSISTENCY_INTENT.test(lower)) {
    logQuery(text, "consistency", firmSlug);
    if (firmObj) {
      const rule = firmObj.consistency_rule || "None / not specified";
      const icon = (!rule || rule.toLowerCase().includes("none")) ? "\u2705 No consistency rule" : `\u26a0\ufe0f ${rule}`;
      const answer = buildFirmRuleAnswer(firmObj, "Consistency Rule", icon, "\ud83d\udcca", getFirmHighlights(firmObj));
      return addBot(answer, [
        { label: `${firmObj.name} profile`, action: "link", data: `/firms/${firmObj.slug}` },
        { label: "All firms' consistency", action: "link", data: "/prop-firms-without-consistency-rule" },
        { label: "Main menu", action: "home" },
      ]);
    }
    const noCons = f.filter(ff => !ff.consistency_rule || ff.consistency_rule.toLowerCase().includes("none"));
    const withCons = f.filter(ff => ff.consistency_rule && !ff.consistency_rule.toLowerCase().includes("none"));
    let answer = "";
    if (noCons.length) { answer += `**No consistency rule (eval):**\n`; noCons.forEach(ff => { answer += `\u2022 ${ff.name}\n`; }); }
    if (withCons.length) { answer += `\n**Has consistency rule:**\n`; withCons.forEach(ff => { answer += `\u2022 ${ff.name} \u2014 ${formatConsistency(ff)}\n`; }); }
    return addBot(answer, [
      { label: "No consistency rule firms", action: "link", data: "/prop-firms-without-consistency-rule" },
      { label: "Help me pick a firm", action: "flow_match" },
      { label: "Main menu", action: "home" },
    ]);
  }

  // ═══ DRAWDOWN TYPE ═══
  if (DRAWDOWN_INTENT.test(lower)) {
    logQuery(text, "drawdown", firmSlug);
    if (firmObj) {
      const fd = formatFirmDisplay(firmObj);
      const answer = buildFirmRuleAnswer(firmObj, fd.drawdownLabel, fd.drawdownFull, "\ud83d\udcc9", getFirmHighlights(firmObj, "low-drawdown"));
      return addBot(answer, [
        { label: `${firmObj.name} profile`, action: "link", data: `/firms/${firmObj.slug}` },
        { label: "All firms by drawdown", action: "link", data: "/trailing-drawdown-prop-firms" },
        { label: "Drawdown calculator", action: "link", data: "/drawdown-calculator" },
        { label: "Main menu", action: "home" },
      ]);
    }
    const grouped = {};
    f.forEach(ff => { const dd = formatFirmDisplay(ff).drawdownShort; if (!grouped[dd]) grouped[dd] = []; grouped[dd].push(ff.name); });
    let answer = `**Firms by drawdown type:**\n\nStatic = floor never moves (most forgiving)\nEOD = updates at close only\nIntraday = updates tick-by-tick (strictest)\n`;
    Object.entries(grouped).forEach(([dd, names]) => { answer += `\n**${dd}:**\n`; names.forEach(n => { answer += `\u2022 ${n}\n`; }); });
    return addBot(answer, [
      { label: "Trailing firms", action: "link", data: "/trailing-drawdown-prop-firms" },
      { label: "EOD firms", action: "link", data: "/eod-drawdown-prop-firms" },
      { label: "Drawdown calculator", action: "link", data: "/drawdown-calculator" },
      { label: "Main menu", action: "home" },
    ]);
  }

  // ═══ NEWS TRADING ═══
  if (NEWS_INTENT.test(lower)) {
    logQuery(text, "news_trading", firmSlug);
    if (firmObj) {
      const allowed = firmObj.news_trading === true;
      const icon = allowed ? "\u2705 Allowed" : firmObj.news_trading === false ? "\u274c Restricted" : "\u2753 Check site";
      const answer = buildFirmRuleAnswer(firmObj, "News Trading", icon, "\ud83d\udcf0", getFirmHighlights(firmObj));
      return addBot(answer, [
        { label: `${firmObj.name} profile`, action: "link", data: `/firms/${firmObj.slug}` },
        { label: "All news trading firms", action: "link", data: "/news-trading-prop-firms" },
        { label: "Main menu", action: "home" },
      ]);
    }
    const allowed = f.filter(ff => ff.news_trading === true);
    const restricted = f.filter(ff => ff.news_trading === false);
    let answer = `**Allow news trading:**\n`;
    allowed.forEach(ff => { answer += `\u2022 ${ff.name}\n`; });
    if (restricted.length) { answer += `\n**Restrict news trading:**\n`; restricted.forEach(ff => { answer += `\u2022 ${ff.name}\n`; }); }
    return addBot(answer, [
      { label: "News trading firms", action: "link", data: "/news-trading-prop-firms" },
      { label: "Help me pick a firm", action: "flow_match" },
      { label: "Main menu", action: "home" },
    ]);
  }

  // ═══ SCALPING ═══
  if (SCALP_INTENT.test(lower)) {
    logQuery(text, "scalping", firmSlug);
    if (firmObj) {
      const hold = firmObj.min_hold_seconds === 0 || firmObj.min_hold_seconds === null ? "None (scalp OK)" : `${firmObj.min_hold_seconds >= 60 ? Math.round(firmObj.min_hold_seconds/60) + " min" : firmObj.min_hold_seconds + "s"} minimum`;
      const icon = (firmObj.min_hold_seconds === 0 || firmObj.min_hold_seconds === null) ? "\u2705" : "\u26a0\ufe0f";
      const answer = buildFirmRuleAnswer(firmObj, "Scalping", `${icon} Min hold: ${hold}`, "\u26a1", getFirmHighlights(firmObj));
      return addBot(answer, [
        { label: `${firmObj.name} profile`, action: "link", data: `/firms/${firmObj.slug}` },
        { label: "All scalping-friendly firms", action: "link", data: "/best-prop-firms-for-scalping" },
        { label: "Main menu", action: "home" },
      ]);
    }
    const scalpOk = f.filter(ff => ff.min_hold_seconds === 0 || ff.min_hold_seconds === null || ff.min_hold_seconds === undefined);
    const withHold = f.filter(ff => ff.min_hold_seconds && ff.min_hold_seconds > 0);
    let answer = `**Scalp-friendly (no hold time):**\n`;
    scalpOk.forEach(ff => { answer += `\u2022 ${ff.name} \u2014 ${formatFirmDisplay(ff).drawdownShort}\n`; });
    if (withHold.length) { answer += `\n**Hold time restrictions:**\n`; withHold.forEach(ff => { answer += `\u2022 ${ff.name} \u2014 ${formatFirmDisplay(ff).minHoldTime}\n`; }); }
    return addBot(answer, [
      { label: "Best for scalping", action: "link", data: "/best-prop-firms-for-scalping" },
      { label: "Help me pick", action: "flow_match" },
      { label: "Main menu", action: "home" },
    ]);
  }

  // ═══ SCALING PLAN ═══
  if (SCALE_INTENT.test(lower)) {
    logQuery(text, "scaling", firmSlug);
    let answer = `**Scaling plans vary by firm.** Most firms allow account growth after consistent profitability.\n\nCheck individual firm profiles for specific scaling terms:\n`;
    f.forEach(ff => { answer += `\u2022 ${ff.name} \u2014 see profile\n`; });
    return addBot(answer, [
      { label: "Compare all firms", action: "link", data: "/futures-prop-firm-comparison" },
      { label: "Help me pick", action: "flow_match" },
      { label: "Main menu", action: "home" },
    ]);
  }

  // ═══ PROFIT SPLIT ═══
  if (SPLIT_INTENT.test(lower)) {
    logQuery(text, "split", firmSlug);
    if (firmObj) {
      const answer = buildFirmRuleAnswer(firmObj, "Profit Split", firmObj.profit_split || "Check site", "\ud83d\udcb0", getFirmHighlights(firmObj, "high-profit-split"));
      return addBot(answer, [
        { label: `${firmObj.name} profile`, action: "link", data: `/firms/${firmObj.slug}` },
        { label: "Compare all splits", action: "link", data: "/futures-prop-firm-comparison" },
        { label: "Main menu", action: "home" },
      ]);
    }
    const sorted = [...f].sort((a, b) => {
      const ex = s => { const m = (s || "").match(/(\d+)/); return m ? parseInt(m[1]) : 0; };
      return ex(b.profit_split) - ex(a.profit_split);
    });
    let answer = `**Firms by profit split (highest first):**\n`;
    sorted.forEach(ff => { answer += `\u2022 ${ff.name} \u2014 ${ff.profit_split || "See site"}\n`; });
    return addBot(answer, [
      { label: "Full comparison", action: "link", data: "/futures-prop-firm-comparison" },
      { label: "Help me pick", action: "flow_match" },
      { label: "Main menu", action: "home" },
    ]);
  }

  // ═══ ACCOUNT SIZE ═══
  if (SIZE_INTENT.test(lower)) {
    logQuery(text, "account_size", firmSlug);
    const size = extractSize(lower);
    if (size) {
      const sizeLabel = numToSizeLabel(size);
      const matching = f.filter(ff => ff.pricing?.some(p => getSizeNumeric(p) === size));
      let answer = `**Firms offering ${sizeLabel} accounts:**\n`;
      matching.forEach(ff => { const p = findByNumericSize(ff.pricing, size); answer += `\u2022 ${ff.name} \u2014 ${p ? formatFirmDisplay(ff, p).priceFormatted : "See site"}\n`; });
      if (!matching.length) answer = `No firms currently list a ${sizeLabel} account in our data. Check the comparison page for all available sizes.`;
      return addBot(answer, [
        { label: "Compare all firms", action: "link", data: "/futures-prop-firm-comparison" },
        { label: "Help me pick", action: "flow_match" },
        { label: "Main menu", action: "home" },
      ]);
    }
    let answer = `**Account sizes across firms:**\n`;
    f.forEach(ff => { const sizes = (ff.pricing || []).map(p => p.size).join(", "); answer += `\u2022 ${ff.name} \u2014 ${sizes || "See site"}\n`; });
    return addBot(answer, [
      { label: "Full comparison", action: "link", data: "/futures-prop-firm-comparison" },
      { label: "Main menu", action: "home" },
    ]);
  }

  // ═══ EA / BOT ═══
  if (EA_INTENT.test(lower)) {
    logQuery(text, "ea_bot", firmSlug);
    if (firmObj) {
      const allowed = firmObj.algos_allowed === true;
      const icon = allowed ? "\u2705 Allowed" : firmObj.algos_allowed === false ? "\u274c Restricted" : "\u2753 Check site";
      const answer = buildFirmRuleAnswer(firmObj, "Algos / EAs", icon, "\ud83e\udd16", getFirmHighlights(firmObj));
      return addBot(answer, [
        { label: `${firmObj.name} profile`, action: "link", data: `/firms/${firmObj.slug}` },
        { label: "All firms' EA policies", action: "link", data: "/futures-prop-firm-rules" },
        { label: "Main menu", action: "home" },
      ]);
    }
    const allowed = f.filter(ff => ff.algos_allowed === true);
    const restricted = f.filter(ff => ff.algos_allowed === false);
    let answer = `**Allow algos/EAs:**\n`;
    allowed.forEach(ff => { answer += `\u2022 ${ff.name}\n`; });
    if (restricted.length) { answer += `\n**Restrict algos/EAs:**\n`; restricted.forEach(ff => { answer += `\u2022 ${ff.name}\n`; }); }
    return addBot(answer, [
      { label: "Full rules guide", action: "link", data: "/futures-prop-firm-rules" },
      { label: "Help me pick", action: "flow_match" },
      { label: "Main menu", action: "home" },
    ]);
  }

  // ═══ COPY TRADING ═══
  if (COPY_INTENT.test(lower)) {
    logQuery(text, "copy_trading", firmSlug);
    let answer = `**Copy trading policies vary by firm.** Most firms restrict or prohibit trade copiers and signal mirroring.\n\nCheck individual firm rules for specifics:\n`;
    f.forEach(ff => { answer += `\u2022 ${ff.name} \u2014 see rules\n`; });
    return addBot(answer, [
      { label: "Full rules guide", action: "link", data: "/futures-prop-firm-rules" },
      { label: "Help me pick", action: "flow_match" },
      { label: "Main menu", action: "home" },
    ]);
  }

  // ═══ COMMUNITY SENTIMENT / TRUST ═══
  if (SENTIMENT_INTENT.test(lower)) {
    logQuery(text, "sentiment", firmSlug);
    if (firmObj) {
      const fd = formatFirmDisplay(firmObj);
      const lines = [`**${firmObj.name} \u2014 Community Trust Overview**\n`];
      lines.push(`Trader sentiment is tracked daily from public discussions on X (Twitter) in our Community Pulse \u2014 covering payout reliability, pricing, rules, and support.`);
      if (firmObj.verified_trader_rating) lines.push(`\n\u2b50 Verified Trader Rating: ${firmObj.verified_trader_rating}/5${firmObj.verified_trader_rating_count ? ` (based on ${firmObj.verified_trader_rating_count} trader mentions)` : ""}`);
      const highlights = getFirmHighlights(firmObj);
      if (highlights.length) { lines.push(`\nKey facts:`); highlights.forEach(h => lines.push(`\u2022 ${h}`)); }
      if (fd.discountValid) lines.push(`\n_${firmObj.name} has an active ${fd.discountPercent}% discount right now._`);
      return addBot(lines.join("\n"), [
        { label: `${firmObj.name} Community Pulse`, action: "link", data: "/community-pulse" },
        { label: `Get ${firmObj.name} deal`, action: "link", data: `/${firmObj.slug}-discount` },
        { label: "Compare firms", action: "flow_compare" },
        { label: "Main menu", action: "home" },
      ]);
    }
    // All firms — direct to live Community Pulse (sentiment-based, no star ratings)
    const answer = `**Community trust & sentiment**\n_FuturesFury tracks live trader sentiment from public discussions on X (Twitter) \u2014 not star ratings. See payout reliability and trader feedback in Community Pulse._\n`;
    return addBot(answer, [
      { label: "See live community feedback", action: "link", data: "/community-pulse" },
      { label: "Payout status tracker", action: "link", data: "/prop-firm-payout-status" },
      { label: "See all current deals", action: "flow_deals" },
      { label: "Main menu", action: "home" },
    ]);
  }

  // ═══ GOTCHAS / CONS / PITFALLS ═══
  if (GOTCHA_INTENT.test(lower)) {
    logQuery(text, "gotcha", firmSlug);
    if (firmObj) {
      const lines = [`**${firmObj.name} \u2014 Key Gotchas & Watch-Outs:**\n`];
      const cons = firmObj.cons || [];
      if (cons.length) {
        cons.forEach(c => lines.push(`\u26a0\ufe0f ${c}`));
      }
      // Add activation fee if notable
      if (firmObj.activation_fee && !firmObj.activation_fee.toLowerCase().includes("$0") && !firmObj.activation_fee.toLowerCase().includes("none")) {
        const alreadyMentioned = cons.some(c => c.toLowerCase().includes("activation"));
        if (!alreadyMentioned) lines.push(`\ud83d\udcb5 Activation fee: ${firmObj.activation_fee}`);
      }
      // Drawdown type warning
      const fd = formatFirmDisplay(firmObj);
      if (fd.drawdownShort?.toLowerCase().includes("trailing")) {
        const alreadyMentioned = cons.some(c => c.toLowerCase().includes("trailing") || c.toLowerCase().includes("drawdown"));
        if (!alreadyMentioned) lines.push(`\ud83d\udcc9 Drawdown: ${fd.drawdownShort} (moves with your P&L)`);
      }
      if (!cons.length && lines.length <= 1) {
        lines.push("No major gotchas documented for this firm.");
      }
      lines.push(`\nSee the full profile for detailed rules and requirements.`);
      return addBot(lines.join("\n"), [
        { label: `${firmObj.name} profile`, action: "link", data: `/firms/${firmObj.slug}` },
        { label: `${firmObj.name} rules`, action: "link", data: `/firms/${firmObj.slug}#rules` },
        { label: "Compare firms", action: "flow_compare" },
        { label: "Main menu", action: "home" },
      ]);
    }
    // General gotchas across all firms
    let answer = `**Common gotchas across prop firms:**\n`;
    answer += `\n**Consistency rules** (most common catch):\n`;
    f.filter(ff => ff.consistency_rule && !ff.consistency_rule.toLowerCase().includes("none")).forEach(ff => {
      answer += `\u2022 ${ff.name} \u2014 ${ff.consistency_pct ? ff.consistency_pct + "%" : ff.consistency_rule.slice(0, 50)}\n`;
    });
    answer += `\n**Activation fees** (hidden cost after passing):\n`;
    f.filter(ff => ff.activation_fee && !ff.activation_fee.toLowerCase().includes("$0") && !ff.activation_fee.toLowerCase().includes("none")).forEach(ff => {
      answer += `\u2022 ${ff.name} \u2014 ${ff.activation_fee.slice(0, 60)}\n`;
    });
    const noNews = f.filter(ff => ff.news_trading === false);
    if (noNews.length) {
      answer += `\n**News trading restricted:**\n`;
      noNews.forEach(ff => { answer += `\u2022 ${ff.name}\n`; });
    }
    return addBot(answer, [
      { label: "True Cost comparison", action: "link", data: "/true-cost-prop-firm-comparison" },
      { label: "Rules guide", action: "link", data: "/futures-prop-firm-rules" },
      { label: "Help me pick", action: "flow_match" },
      { label: "Main menu", action: "home" },
    ]);
  }

  // ═══ TIME LIMIT ═══
  if (TIME_LIMIT_INTENT.test(lower)) {
    logQuery(text, "time_limit", firmSlug);
    if (firmObj) {
      const limit = firmObj.time_limit;
      const hasLimit = limit && !["none", "no", "unlimited", "no limit"].some(x => String(limit).toLowerCase().includes(x));
      const icon = hasLimit ? `\u23f0 ${limit}` : "\u2705 No time limit";
      const lines = [`**${firmObj.name}**\n`, `${icon}\n`];
      if (!hasLimit) lines.push(`You can take as long as you need to pass the evaluation.`);
      const highlights = getFirmHighlights(firmObj);
      if (highlights.length) { lines.push(`\nWhy traders choose ${firmObj.name.split(" ")[0]}:`); highlights.forEach(h => lines.push(`\u2022 ${h}`)); }
      return addBot(lines.join("\n"), [
        { label: `${firmObj.name} profile`, action: "link", data: `/firms/${firmObj.slug}` },
        { label: "No time limit firms", action: "link", data: "/prop-firms-no-time-limit" },
        { label: "Main menu", action: "home" },
      ]);
    }
    const noLimit = f.filter(ff => !ff.time_limit || ["none", "no", "unlimited", "no limit"].some(x => String(ff.time_limit).toLowerCase().includes(x)));
    const withLimit = f.filter(ff => ff.time_limit && !["none", "no", "unlimited", "no limit"].some(x => String(ff.time_limit).toLowerCase().includes(x)));
    let answer = "";
    if (noLimit.length === f.length) {
      answer = `**Good news \u2014 none of our tracked firms impose a hard time limit on evaluations.**\n\nYou can take as long as you need to pass.\n`;
      noLimit.forEach(ff => { answer += `\u2022 ${ff.name} \u2014 No limit\n`; });
    } else {
      if (noLimit.length) { answer += `**No time limit:**\n`; noLimit.forEach(ff => { answer += `\u2022 ${ff.name}\n`; }); }
      if (withLimit.length) { answer += `\n**Has time limit:**\n`; withLimit.forEach(ff => { answer += `\u2022 ${ff.name} \u2014 ${ff.time_limit}\n`; }); }
    }
    return addBot(answer, [
      { label: "No time limit firms", action: "link", data: "/prop-firms-no-time-limit" },
      { label: "Help me pick", action: "flow_match" },
      { label: "Main menu", action: "home" },
    ]);
  }

  // ═══ FLOW ROUTING ═══
  if (MARKET_PULSE_INTENT.test(lower)) { logQuery(text, "market_pulse", null); return startMarketPulse(); }
  if (BEGINNER_INTENT.test(lower)) { logQuery(text, "beginner", firmSlug); return startBeginnerFlow(); }
  if (RULE_INTENT.test(lower)) { logQuery(text, "rule_explain", firmSlug); return startRulesFlow(); }
  if (TOOL_INTENT.test(lower)) { logQuery(text, "tools", firmSlug); return startToolsFlow(); }
  if (lower.match(/pick|recommend|help me|find.*firm/)) { logQuery(text, "match", firmSlug); return startMatchFlow(); }

  // ═══ FIRM NAME FALLBACK ═══
  if (firmSlug && firmObj) {
    logQuery(text, "firm_lookup", firmSlug);
    const fd = formatFirmDisplay(firmObj);
    if (fd.discountValid) {
      addBot(`Looking up **${firmObj.name}** \u2014 they have an active ${fd.discountPercent}% discount right now.`, []);
    }
    return showFirmDeal(firmObj.slug);
  }

  // ═══ UNKNOWN INTENT ═══
  logQuery(text, "unknown", null);
  // Instead of dead end, surface actionable options
  const topFirms = [...f].sort((a, b) => (b.verified_trader_rating || b.rating || 0) - (a.verified_trader_rating || a.rating || 0)).slice(0, 3);
  const topNames = topFirms.map(ff => ff.name).join(", ");
  addBot(`I couldn't find an exact match for that, but I can help you decide. Try one of these:\n\n\u2022 **Compare firms** \u2014 see them side by side\n\u2022 **Find deals** \u2014 check active discounts\n\u2022 **Help me pick** \u2014 I'll match you based on your style\n\nOr ask about a specific firm: ${topNames}`, [
    { label: "Find best firm for you", action: "flow_match" },
    { label: "See all current deals", action: "flow_deals" },
    { label: "Compare firms", action: "flow_compare" },
    { label: "Explain rules", action: "flow_rules" },
  ]);
}
